/* eslint-disable i18next/no-literal-string */
import { useQuery } from '@apollo/react-hooks';
import _get from 'lodash/get';
import _map from 'lodash/map';
import _filter from 'lodash/filter';
import _includes from 'lodash/includes';
import _isEqual from 'lodash/isEqual';
import _kebabCase from 'lodash/kebabCase';
import { useObserver } from 'mobx-react-lite';

import { parseProduct, getProductInventoryFilters } from 'utils/helpers/product';

import { CategoryPotencyRanges, CategoryPotencyUnits, POTENCY_UNITS } from 'shared/constants';
import filteredProducts from 'shared/graphql/product/filtered-products.gql';

import { getCategory, useDispensaryCategory } from 'src/hooks/use-dispensary-category';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useDispensaryFlag from 'shared/hooks/use-dispensary-flag';
import useUI from 'hooks/use-ui';
import useProductQueryParams from 'hooks/use-product-query-params';
import useMenuFilterOptions from 'hooks/use-menu-filter-options';
import useCart from 'hooks/use-cart';
import { useHidePotenciesAndStrains } from 'hooks/use-hide-potencies-and-strains';
import { useDispensarySubcategory } from 'src/hooks/use-dispensary-subcategory';

const sortQueryParamToFields = (param) => {
  switch (param) {
    case 'pricehightolow':
      return { sortDirection: -1, sortBy: 'price' };
    case 'pricelowtohigh':
      return { sortDirection: 1, sortBy: 'price' };
    case 'potencyhightolow':
      return { sortDirection: -1, sortBy: 'potency' };
    case 'potencylowtohigh':
      return { sortDirection: 1, sortBy: 'potency' };
    case 'custom':
      return { sortDirection: 1, sortBy: 'weight' };
    case 'category':
      return { sortDirection: 1, sortBy: 'type' };
    default:
      return { sortDirection: 1, sortBy: param };
  }
};

export function getPotencyContentForFilter(range, category) {
  const unit = CategoryPotencyUnits[category?.key] === `%` ? POTENCY_UNITS.PERCENTAGE : POTENCY_UNITS.MILLIGRAMS;
  if (range?.length === 2 && category?.key) {
    // If this is the default range for the category then don't apply any filtering
    if (_isEqual(CategoryPotencyRanges[category.key], range)) {
      return undefined;
    }
    // If the upper query param value is the max limit for the category,
    // include products with potencies that surpass the maximum.
    // e.g. if a pre-roll's potency is 60%, it should still show if the selected
    // potency filter range is [20, 50] even though the max range for pre-rolls is 50%.
    if (range[1] === CategoryPotencyRanges[category.key]?.[1]) {
      return { range: [range[0], Number.MAX_SAFE_INTEGER], unit };
    }
    return { range, unit };
  }
  return undefined;
}

export default function useDispensaryProducts(props) {
  const { filters, perPage = 50, useBatch = false } = props || {};
  let { skip = false } = props || {};
  const { queryParams: productQueryParams } = useProductQueryParams();
  const { brands: loadedBrands } = useMenuFilterOptions({
    skipCurrentFilters: true,
    location: 'use-dispensary-products.js',
  });
  const category = useDispensaryCategory();
  const { subcategory } = useDispensarySubcategory();
  const { dispensary } = useDispensary();
  const cart = useCart();
  const { isKiosk } = useUI();
  const menuType = useObserver(() => cart.menuType);
  const { menuHidePotencies } = useHidePotenciesAndStrains();

  const {
    search: query,
    weight: option,
    brands: selectedBrands,
    page,
    strainTypes,
    subcategories: subcategoriesFromQueryParams,
    category: categoryFromParams,
  } = productQueryParams;
  let sortByQueryParams = productQueryParams.sortby;

  // When no sorting option is selected, but a query is made we should sort by match score
  // Otherwise, default to to the dispensary's default sort order
  let isDefaultSort = false;
  if (sortByQueryParams === 'default' && query) {
    sortByQueryParams = null;
    isDefaultSort = true;
  } else if (sortByQueryParams === 'default' && dispensary) {
    sortByQueryParams = dispensary.menuOrder;
    isDefaultSort = true;
  }

  const thcContentForFilter = getPotencyContentForFilter(productQueryParams.potencyTHC, category);
  const cbdContentForFilter = getPotencyContentForFilter(productQueryParams.potencyCBD, category);

  let types = [];

  if (filters?.category) {
    types = [filters.category];
  } else {
    types = query || !category || (category && category.key === 'all') ? [] : [category.value];
  }

  if (filters?.types?.length > 0) {
    types = filters.types;
  }

  if (categoryFromParams) {
    types = [getCategory(categoryFromParams).value];
  }

  let brandIds = filters?.brandIds;
  const filterableBrands = filters?.brands || selectedBrands;

  if (filterableBrands?.length) {
    // get all brand objects that match brand names from filter, accounts for duplicate brands
    const matchingBrands = _filter(loadedBrands, (brand) => _includes(filterableBrands, _kebabCase(brand.name)));

    skip = !loadedBrands?.length;
    brandIds = _map(matchingBrands, (brand) => brand?.id);
  }

  const shouldUseNewInventoryFiltering = useDispensaryFlag(`use-updated-inventory-filtering`, dispensary?.id);

  const productsFilter = {
    brandIds,
    productIds: filters?.productIds,
    staffPicks: filters?.staffPicks,
    dispensaryId: dispensary?.id,
    option,
    pricingType: menuType,
    strainTypes: filters?.strainTypes || strainTypes,
    subcategories: filters?.subcategories || subcategory || subcategoriesFromQueryParams,
    Status: `Active`,
    CBDContent: cbdContentForFilter,
    THCContent: thcContentForFilter,
    isOnSpecial: filters?.isOnSpecial,
    specialId: filters?.specialId,
    specialType: filters?.specialType,
    types,
    query,
    useCache: filters?.useCache || false,
    ...sortQueryParamToFields(filters?.sortBy || sortByQueryParams),
    isDefaultSort,
    bypassOnlineThresholds: isKiosk,
    isKioskMenu: isKiosk,
    ...(shouldUseNewInventoryFiltering
      ? { inventoryFilters: getProductInventoryFilters({ dispensary, isKiosk }) }
      : { removeProductsBelowOptionThresholds: true }),
  };

  const variables = {
    productsFilter,
    includeCannabinoids: !menuHidePotencies,
    page: (page || 1) - 1,
    perPage: perPage > 0 ? perPage : undefined,
  };

  const { data, ...apolloValues } = useQuery(filteredProducts, {
    skip: !dispensary || skip,
    variables,
    context: {
      useBatch,
    },
  });

  const products = _map(_get(data, 'filteredProducts.products', []), parseProduct);
  const totalCount = _get(data, 'filteredProducts.queryInfo.totalCount', 0);
  const totalPages = _get(data, 'filteredProducts.queryInfo.totalPages');

  return { products, totalCount, totalPages, data, ...apolloValues };
}
